import { isFiniteCard } from "../game/deckEngine";
import { normalizeStoryItemId } from "./storyState";
import { getStoryBattleRarityXp } from "./storyModeBattleXPUtils";

const STORY_BATTLE_TOKEN_WIN_RESULT_KEYS = new Set([
  "story_side_win",
  "story_side_timeout_win",
]);

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeTextKey = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeUsedCardSourceId = (card = {}) =>
  normalizeStoryItemId(
    typeof card === "string"
      ? card
      : card?.sourceId || card?.id || card?.name || "",
  );

const getMatchPlayers = (matchData = {}) => toArray(matchData?.players).filter(Boolean);

const getMatchMoves = (matchData = {}) => toArray(matchData?.moves).filter(Boolean);

const findCatalogInfinityCard = (card = {}, catalogInfinityCards = []) => {
  const normalizedCandidates = [
    card?.sourceId,
    card?.id,
    card?.name,
  ]
    .filter(Boolean)
    .map(normalizeUsedCardSourceId)
    .filter(Boolean);
  if (!normalizedCandidates.length) return null;

  return (
    toArray(catalogInfinityCards).find((catalogCard) => {
      const catalogCandidates = [
        catalogCard?.sourceId,
        catalogCard?.id,
        catalogCard?.name,
      ]
        .filter(Boolean)
        .map(normalizeUsedCardSourceId)
        .filter(Boolean);
      return catalogCandidates.some((candidate) =>
        normalizedCandidates.includes(candidate),
      );
    }) || null
  );
};

const getEnemyPlayers = ({ battle = null, matchData = {} } = {}) => {
  const storySideId = String(battle?.storySide?.sideId || "red").trim();

  return getMatchPlayers(matchData).filter(
    (player) => String(player?.team || "").trim() !== storySideId,
  );
};

export const isEligibleStoryBattleTokenRewardResult = ({
  resultKey = "",
} = {}) => STORY_BATTLE_TOKEN_WIN_RESULT_KEYS.has(normalizeTextKey(resultKey));

export const buildStoryBattleTokenReward = ({
  roomId = "",
  missionId = "",
  encounterId = "",
  battle = null,
  matchData = {},
  resultKey = "",
} = {}) => {
  const claimKey = String(roomId || "").trim() || null;
  const enemyPlayers = getEnemyPlayers({ battle, matchData });
  const rarityTokens = enemyPlayers.reduce(
    (total, player) => total + getStoryBattleRarityXp(player?.character?.rarity),
    0,
  );
  const eligible =
    Boolean(claimKey) &&
    isEligibleStoryBattleTokenRewardResult({
      resultKey,
    });

  return {
    claimKey,
    missionId: String(missionId || "").trim() || null,
    encounterId: String(encounterId || "").trim() || null,
    totalTokens: eligible ? rarityTokens : 0,
    rarityTokens,
    enemyCount:
      enemyPlayers.length || toArray(battle?.opponentSide?.opponentRoles).length,
    eligible,
  };
};

export const buildUsedStoryInfinityCards = ({
  matchData = {},
  userId = "",
  catalogInfinityCards = [],
} = {}) => {
  const normalizedUserId = String(userId || "").trim();
  if (!normalizedUserId) return [];

  // Story pack reconciliation is tied to the local player's actual card plays.
  // The move log is the reliable source of truth here because it records every
  // explicit playCard action even after the hand/deck state has changed.
  const usedCardsBySourceId = new Map();

  getMatchMoves(matchData).forEach((move) => {
    if (normalizeTextKey(move?.type) !== "playcard") return;
    if (String(move?.actorId || "").trim() !== normalizedUserId) return;

    const moveCard = move?.card || null;
    const sourceId = normalizeUsedCardSourceId(moveCard);
    if (!sourceId) return;

    const catalogCard = findCatalogInfinityCard(moveCard, catalogInfinityCards);
    const resolvedCard = catalogCard || moveCard || {};
    const fullyConsumable = isFiniteCard(resolvedCard);

    const existingEntry = usedCardsBySourceId.get(sourceId);
    if (existingEntry) {
      existingEntry.timesPlayed += 1;
      return;
    }

    usedCardsBySourceId.set(sourceId, {
      sourceId,
      id: resolvedCard?.id || moveCard?.id || sourceId,
      name: resolvedCard?.name || moveCard?.name || sourceId,
      imageUrl: resolvedCard?.imageUrl || moveCard?.imageUrl || "",
      timesPlayed: 1,
      fullyConsumable,
      duplicateConsumable: !fullyConsumable,
    });
  });

  return [...usedCardsBySourceId.values()];
};
